import { useEffect, useRef } from "react"
import { useLocation, useMatches } from "react-router-dom"
import { useSavePreference } from "hooks/preferences"
import { LAST_LANDING_PREFERENCE_KEY, LAST_LANDING_PREFERENCE_TYPE, SURFACE_TYPES } from "./landing-preference"
import { useAccountAlias } from "resources/account"

export function useSyncLastLandingFromRoute(accountAlias, atRootMatch, { enabled = true } = {}) {
  const location = useLocation()
  const matches = useMatches()
  const saveLastLandingPreference = useSavePreference(LAST_LANDING_PREFERENCE_KEY, LAST_LANDING_PREFERENCE_TYPE)
  const loadedAccountAlias = useAccountAlias()
  const lastSavedPreferenceKeyRef = useRef(null)
  const dedupeScopeRef = useRef("")

  useEffect(() => {
    // accountAlias is from the URL; loadedAccountAlias is pulled once the account loads
    if (!enabled || !accountAlias || accountAlias !== loadedAccountAlias || atRootMatch) {
      return
    }

    if (dedupeScopeRef.current !== accountAlias) {
      dedupeScopeRef.current = accountAlias
      lastSavedPreferenceKeyRef.current = null
    }

    const matchWithLandingSurface = [...matches].reverse().find(match => match.handle?.landingSurface)

    if (!matchWithLandingSurface) {
      return
    }

    const landingSurfaceType = matchWithLandingSurface.handle.landingSurface

    let nextPreference
    if (landingSurfaceType === SURFACE_TYPES.DASHBOARD) {
      nextPreference = { type: SURFACE_TYPES.DASHBOARD }
    } else if (landingSurfaceType === SURFACE_TYPES.PORTFOLIO && matchWithLandingSurface.params?.portfolioId) {
      nextPreference = {
        type: SURFACE_TYPES.PORTFOLIO,
        portfolioId: matchWithLandingSurface.params.portfolioId,
      }
    } else {
      return
    }

    const preferenceKey = JSON.stringify(nextPreference)
    if (lastSavedPreferenceKeyRef.current === preferenceKey) {
      return
    }

    lastSavedPreferenceKeyRef.current = preferenceKey
    saveLastLandingPreference(nextPreference)
  }, [enabled, accountAlias, atRootMatch, matches, location.pathname, saveLastLandingPreference, loadedAccountAlias])
}
